import * as React from 'react';

import { useSupplierDashboard } from '@/api/use-supplier-dashboard';
import type {
  DashboardParticipationDto,
  DashboardPhaseDto,
  SubmissionStateDto,
} from '@/api/supplier-dashboard-client';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

// =============================================================================
// dashboard-screen.tsx — Tableau de bord FOURNISSEUR (DBU §3.2, Lot A / D3b, S20-REMEDIATION).
// =============================================================================
// GET /supplier/dashboard — point d'entrée fournisseur post-auth. Sous-ensemble Lot A (SANS MIGRATION) :
//   • Focus Campagne (module haut : campagne active la plus urgente, phase, échéance, action) ;
//   • Vue tabulaire des sollicitations actives (intitulé, date de lancement, compte à rebours, complétion) ;
//   • Progression (taux de complétion des profils requis, ex. 42/82) ;
//   • Mention « Aucune campagne … » hors campagne.
// RETIRÉ (GATE_HUMAN 2026-09-11) : Centre d'alertes (NotificationCenterWidget), Navigation rapide
// (catalogue accessible via la sidebar) et Historique des participations.
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope>, phase/criticité en LIBELLÉ TEXTE (badge = renfort,
// jamais seul porteur d'info) ; barre de progression role="progressbar" + aria-valuetext + texte visible ;
// messages role="status"/role="alert" ; navigation clavier native (boutons/liens). Tokens sémantiques
// shadcn/Tailwind uniquement.

export interface SupplierDashboardScreenProps {
  /**
   * Ouvre le formulaire de saisie d'une submission (navigation portée par l'adaptateur routeur).
   * `cycle` = numéro de cycle de la participation (DBU §3.5) : propagé à la saisie (search param `cycle`)
   * pour activer le Cycle 2 (rappel C1 + écart C1↔C2 + indicateur §3.6) quand cycle ≥ 2.
   */
  readonly onOpenCampaign: (campaignId: string) => void;
}

/** Libellé FR de la phase opérationnelle (DBU §3.2 « Cycle de vie et statuts contextuels »). */
function phaseLabel(phase: DashboardPhaseDto): string {
  switch (phase) {
    case 'Ouverture':
      return 'Saisie ouverte';
    case 'Analyse':
      return 'Analyse en cours';
    case 'Negociation':
      return 'Négociation';
    case 'Cloture':
      return 'Clôturée';
  }
}

/** Libellé de l'action de saisie selon la phase + l'état de la submission (DBU §3.2). */
function actionLabel(
  phase: DashboardPhaseDto,
  submissionState: SubmissionStateDto,
): string {
  if (phase === 'Analyse') {
    return 'Consulter mes tarifs';
  }
  if (phase === 'Negociation') {
    return 'Reprendre ma saisie';
  }
  // Ouverture.
  if (submissionState === 'Soumis') {
    return 'Modifier mes tarifs';
  }
  if (submissionState === 'Vide') {
    return 'Saisir mes tarifs';
  }
  return 'Reprendre ma saisie';
}

/** Nombre de jours entiers restant avant l'échéance (ou null si non planifiée). */
function daysUntil(closeAt: string | null, now: Date): number | null {
  if (closeAt === null) {
    return null;
  }
  const diffMs = new Date(closeAt).getTime() - now.getTime();
  return Math.ceil(diffMs / 86_400_000);
}

/** Date FR courte « 11 sept. » (jour + mois abrégé, sans année) — statuts d'échéance inline. */
function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    month: 'short',
    day: 'numeric',
  });
}

/** Tonalité d'échéance (point coloré + texte court) — rouge dépassée, ambre proche/aujourd'hui, sinon neutre. */
type EcheanceTone = 'passed' | 'today' | 'soon' | 'normal' | 'none';
interface EcheanceInfo {
  readonly tone: EcheanceTone;
  /** Classe de fond du point de statut. */
  readonly dotClass: string;
  /** Classe de couleur du texte. */
  readonly textClass: string;
  /** Libellé court (tableau). */
  readonly tableText: string;
  /** Libellé long (hero). */
  readonly heroText: string;
}

/** Dérive le statut d'échéance (compte à rebours) en point + texte, pour le hero ET le tableau. */
function echeanceInfo(closeAt: string | null, now: Date): EcheanceInfo {
  const days = daysUntil(closeAt, now);
  if (days === null || closeAt === null) {
    return {
      tone: 'none',
      dotClass: 'bg-muted-foreground/40',
      textClass: 'text-muted-foreground',
      tableText: 'Échéance non planifiée',
      heroText: 'Échéance non planifiée',
    };
  }
  if (days < 0) {
    const d = formatDateShort(closeAt);
    return {
      tone: 'passed',
      dotClass: 'bg-destructive',
      textClass: 'text-destructive',
      tableText: `Dépassée · ${d}`,
      heroText: `Échéance dépassée depuis le ${d}`,
    };
  }
  if (days === 0) {
    return {
      tone: 'today',
      dotClass: 'bg-amber-500',
      textClass: 'text-amber-600',
      tableText: "Clôture aujourd'hui",
      heroText: "Clôture aujourd'hui",
    };
  }
  if (days <= 7) {
    return {
      tone: 'soon',
      dotClass: 'bg-amber-500',
      textClass: 'text-amber-600',
      tableText: `J-${String(days)}`,
      heroText: `Clôture dans ${String(days)} j`,
    };
  }
  return {
    tone: 'normal',
    dotClass: 'bg-muted-foreground/40',
    textClass: 'text-muted-foreground',
    tableText: `J-${String(days)}`,
    heroText: `Clôture dans ${String(days)} j`,
  };
}

/** Pourcentage de complétion (profils saisis / profils requis), borné [0, 100]. */
function completionPct(saisis: number, requis: number): number {
  if (requis <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((saisis / requis) * 100));
}

/**
 * Mini-barre de progression accessible (fine). AMBRE tant que la saisie est en cours, NOIRE une fois
 * complète (100 %). role="progressbar" + aria-valuetext (jamais une couleur seule). `className` = largeur.
 */
function MiniBar({
  saisis,
  requis,
  className,
}: {
  readonly saisis: number;
  readonly requis: number;
  readonly className?: string;
}): React.JSX.Element {
  const pct = completionPct(saisis, requis);
  const complete = pct >= 100;
  return (
    <span
      role="progressbar"
      aria-label="Taux de complétion de la saisie"
      aria-valuemin={0}
      aria-valuemax={requis}
      aria-valuenow={saisis}
      aria-valuetext={`${String(saisis)} / ${String(requis)} profils saisis (${String(pct)} %)`}
      className={cn(
        'inline-block h-1.5 overflow-hidden rounded-full bg-muted align-middle',
        className,
      )}
    >
      <span
        className={cn(
          'block h-full rounded-full',
          complete ? 'bg-foreground' : 'bg-amber-500',
        )}
        style={{ width: `${String(pct)}%` }}
      />
    </span>
  );
}

/** Badge de statut UNIFORME (fond ivoire, bordure fine) — phase opérationnelle en libellé texte. */
function StatusBadge({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground">
      {children}
    </span>
  );
}

/** Tag « PRIORITAIRE » (mono, fond ambre clair) — marque la ligne de la campagne mise en hero. */
function PrioriteTag(): React.JSX.Element {
  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-amber-800">
      Prioritaire
    </span>
  );
}

export function SupplierDashboardScreen({
  onOpenCampaign,
}: SupplierDashboardScreenProps): React.JSX.Element {
  const query = useSupplierDashboard();
  // Horloge locale figée au rendu (comptes à rebours). Pas d'état métier persisté (RULE_REF_006).
  const now = React.useMemo(() => new Date(), []);

  return (
    <section
      className="mx-auto flex w-full max-w-[1180px] flex-col gap-6"
      aria-labelledby="dashboard-title"
    >
      <header className="flex flex-col gap-1">
        <h1
          id="dashboard-title"
          className="text-2xl font-semibold tracking-tight text-foreground"
        >
          Tableau de bord
        </h1>
        <p className="text-sm text-muted-foreground">
          Vos sollicitations de référencement en cours et passées.
        </p>
      </header>

      {query.isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du tableau de bord…
        </p>
      ) : null}

      {query.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
        >
          Impossible de charger le tableau de bord : {query.error.message}
        </p>
      ) : null}

      {query.isSuccess ? (
        <DashboardContent
          active={query.data.active}
          now={now}
          onOpenCampaign={onOpenCampaign}
        />
      ) : null}
    </section>
  );
}

function DashboardContent({
  active,
  now,
  onOpenCampaign,
}: {
  readonly active: readonly DashboardParticipationDto[];
  readonly now: Date;
  readonly onOpenCampaign: (campaignId: string) => void;
}): React.JSX.Element {
  const focus = active[0];
  // DBU §3.2 l.2514-2516 : la mention « aucune campagne ouverte » s'affiche dès qu'aucune
  // SOLLICITATION n'est ouverte (aucune campagne ACTIVE), indépendamment de l'historique.
  const noSolicitation = active.length === 0;

  return (
    <div className="flex flex-col gap-6">
      {noSolicitation ? (
        <Card>
          <CardContent className="py-8">
            <p role="status" className="text-center text-sm text-foreground">
              Aucune campagne de référencement n'est actuellement ouverte.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Module Focus Campagne — campagne active la plus prioritaire (échéance la plus proche). */}
      {focus ? (
        <FocusCard focus={focus} now={now} onOpenCampaign={onOpenCampaign} />
      ) : null}

      {/* Vue tabulaire des sollicitations actives (DBU §3.2 « Gestion des sollicitations actives »). */}
      {active.length > 0 ? (
        <Card aria-labelledby="active-title">
          <CardHeader>
            <CardTitle id="active-title" className="flex items-center gap-2">
              Sollicitations en cours
              <span className="font-mono text-sm font-normal tabular-nums text-muted-foreground">
                {active.length}
              </span>
            </CardTitle>
            <CardDescription>
              Cliquez sur une campagne pour saisir ou modifier vos tarifs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Campagnes de référencement en cours auxquelles vous êtes associé
              </caption>
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Campagne
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Lancement
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Échéance
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Avancement
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Statut
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {active.map((p) => {
                  const ech = echeanceInfo(p.cycleCloseAt, now);
                  const isPriority = p.campaignId === focus?.campaignId;
                  return (
                    <tr key={p.campaignId} className="border-b border-border/60">
                      <th scope="row" className="py-3 pr-4 font-medium text-foreground">
                        <span className="flex flex-wrap items-center gap-2">
                          {p.intitule}
                          {isPriority ? <PrioriteTag /> : null}
                        </span>
                      </th>
                      <td className="py-3 pr-4 tabular-nums text-muted-foreground">
                        {formatDate(p.launchAt)}
                      </td>
                      <td className={cn('py-3 pr-4', ech.textClass)}>
                        <span className="inline-flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className={cn(
                              'h-1.5 w-1.5 shrink-0 rounded-full',
                              ech.dotClass,
                            )}
                          />
                          {ech.tableText}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-foreground">
                        <span className="inline-flex items-center gap-2">
                          <MiniBar
                            saisis={p.completion.profilesSaisis}
                            requis={p.completion.profilesRequis}
                            className="w-16"
                          />
                          <span className="font-mono tabular-nums">
                            {p.completion.profilesSaisis} / {p.completion.profilesRequis}
                          </span>
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <StatusBadge>{phaseLabel(p.phase)}</StatusBadge>
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-[150px]"
                          onClick={() => onOpenCampaign(p.campaignId)}
                        >
                          Reprendre
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Carte « Campagne prioritaire » (hero) — DBU §3.2. En-tête : label mono + badge phase·cycle sur une
 * ligne, titre en dessous, actions (Voir la campagne / CTA saisie) alignées à droite. Corps : une seule
 * ligne de statut inline (point d'échéance + filet + progression fine). Redesign GATE_HUMAN 2026-09-12.
 */
function FocusCard({
  focus,
  now,
  onOpenCampaign,
}: {
  readonly focus: DashboardParticipationDto;
  readonly now: Date;
  readonly onOpenCampaign: (campaignId: string) => void;
}): React.JSX.Element {
  const ech = echeanceInfo(focus.cycleCloseAt, now);
  const { profilesSaisis, profilesRequis } = focus.completion;
  const complete = completionPct(profilesSaisis, profilesRequis) >= 100;
  return (
    <Card aria-labelledby="focus-title">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Campagne prioritaire
              </span>
              <StatusBadge>
                {`${phaseLabel(focus.phase)} · cycle ${String(focus.cycleNumber)}`}
              </StatusBadge>
            </div>
            <CardTitle id="focus-title" className="text-xl">
              {focus.intitule}
            </CardTitle>
          </div>
          {/* Action unique : ouvrir la campagne (détail + grille de saisie embarquée). GATE_HUMAN
              2026-09-12 : « Reprendre » mène à /campagnes?campaign=<id> (focus campagne), plus à /saisie. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => onOpenCampaign(focus.campaignId)}>
              {actionLabel(focus.phase, focus.submissionState)}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Ligne de statut UNIQUE : échéance (point coloré) | filet | progression fine. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <span className={cn('inline-flex items-center gap-2', ech.textClass)}>
            <span
              aria-hidden="true"
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', ech.dotClass)}
            />
            {ech.heroText}
          </span>
          <span aria-hidden="true" className="h-4 w-px bg-border" />
          <span className="inline-flex items-center gap-3">
            <span className="text-foreground">
              {complete ? 'Saisie complète' : 'Progression'}
            </span>
            <MiniBar
              saisis={profilesSaisis}
              requis={profilesRequis}
              className="w-24"
            />
            <span className="font-mono tabular-nums text-foreground">
              {profilesSaisis} / {profilesRequis} profils
            </span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Formate une date ISO en date FR courte, ou « — » si absente. */
function formatDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  return new Date(iso).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
